import type { FeedFreshnessReason } from '../core/session';

/**
 * A live socket proves nothing about its streams: a subscription can be closed
 * server-side while pings keep flowing, and a broadcast can be lost after commit.
 * The heartbeat is a slow journal probe that catches every silent loss for the
 * active channel; the session skips the latest snapshot when the journal is quiet.
 */
export const FEED_FRESHNESS_HEARTBEAT_MS = 120_000;

interface FeedLifecycleFreshnessSession {
  ensureFresh(reason: FeedFreshnessReason): Promise<void>;
  invalidateFreshness(reason: string): void;
  persistRestoreNow(reason: 'pagehide'): boolean;
}

interface ElectronSystemBridge {
  onResume(callback: () => void): () => void;
}

interface FeedLifecycleWindow extends Window {
  readonly electron?: { readonly system?: ElectronSystemBridge };
}

/** Wires browser, cable, and optional Electron wake signals to one session freshness owner. */
export function subscribeFeedSessionLifecycleEvents(
  session: FeedLifecycleFreshnessSession,
  lifecycleWindow: FeedLifecycleWindow = window as FeedLifecycleWindow,
  lifecycleDocument: Document = document,
  heartbeatMs: number = FEED_FRESHNESS_HEARTBEAT_MS
): () => void {
  let sawCableDisconnect = false;
  const persist = () => session.persistRestoreNow('pagehide');
  const ensure = (reason: FeedFreshnessReason) => void session.ensureFresh(reason);
  const handleHeartbeat = () => {
    if (lifecycleDocument.visibilityState !== 'visible') return;
    if (lifecycleWindow.navigator?.onLine === false) return;
    ensure('heartbeat');
  };
  const handleFocus = () => ensure('focus');
  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) ensure('pageshow');
  };
  const handleVisibilityChange = () => {
    if (lifecycleDocument.visibilityState === 'hidden') {
      session.invalidateFreshness('hidden');
      persist();
    } else if (lifecycleDocument.visibilityState === 'visible') {
      ensure('visible');
    }
  };
  const handleOffline = () => session.invalidateFreshness('offline');
  const handleOnline = () => ensure('online');
  const handleCable = (event: Event) => {
    const detail = customEventDetail(event);
    const connected = cableConnected(detail);
    if (connected === false) {
      sawCableDisconnect = true;
      session.invalidateFreshness('cable-disconnect');
      return;
    }
    if (connected !== true) return;
    const shouldReconcile = sawCableDisconnect || cableReconnect(detail);
    sawCableDisconnect = false;
    if (shouldReconcile) ensure('cable-reconnect');
  };

  if (lifecycleDocument.visibilityState === 'hidden') session.invalidateFreshness('hidden');
  lifecycleWindow.addEventListener('focus', handleFocus);
  lifecycleWindow.addEventListener('pagehide', persist);
  lifecycleWindow.addEventListener('pageshow', handlePageShow);
  lifecycleWindow.addEventListener('offline', handleOffline);
  lifecycleWindow.addEventListener('online', handleOnline);
  lifecycleWindow.addEventListener('output:internal:cable', handleCable);
  lifecycleDocument.addEventListener('visibilitychange', handleVisibilityChange);
  const unsubscribeResume = lifecycleWindow.electron?.system?.onResume?.(() => ensure('resume'));
  const heartbeat = heartbeatMs > 0 ? setInterval(handleHeartbeat, heartbeatMs) : undefined;

  return () => {
    if (heartbeat !== undefined) clearInterval(heartbeat);
    lifecycleWindow.removeEventListener('focus', handleFocus);
    lifecycleWindow.removeEventListener('pagehide', persist);
    lifecycleWindow.removeEventListener('pageshow', handlePageShow);
    lifecycleWindow.removeEventListener('offline', handleOffline);
    lifecycleWindow.removeEventListener('online', handleOnline);
    lifecycleWindow.removeEventListener('output:internal:cable', handleCable);
    lifecycleDocument.removeEventListener('visibilitychange', handleVisibilityChange);
    unsubscribeResume?.();
  };
}

function customEventDetail(event: Event): unknown | undefined {
  return 'detail' in event ? event.detail : undefined;
}

function cableConnected(detail: unknown): boolean | undefined {
  return isRecord(detail) && typeof detail.connected === 'boolean' ? detail.connected : undefined;
}

function cableReconnect(detail: unknown): boolean {
  return isRecord(detail) && detail.reconnect === true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
